'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { X, Loader2, Lock, DollarSign } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getStocksBatch, cloneCongressionalPortfolio } from '@/lib/api-client';

export interface CloneHolding {
  /** Ticker symbol — required. */
  ticker: string;
  /** Estimated dollar weight of the member's actual position. If provided,
   *  it's used for price-weighted allocation; otherwise we fall back to
   *  equal-weight. */
  estimatedValue?: number;
}

type AllocationStrategy = 'equal' | 'mirror';

/** Round portrait of the member not included here — the parent already shows it
 *  in its header. This component is purely the allocation dialog. */
export default function ClonePortfolioPopup({
  memberId,
  memberName,
  holdings,
  isPro,
  onClose,
  onCreated,
}: {
  /** Member id for the backend `/clone` endpoint (bioGuideId or UUID). */
  memberId?: string;
  memberName: string;
  holdings: CloneHolding[];
  isPro: boolean;
  onClose: () => void;
  onCreated: (portfolioId: string) => void;
}) {
  const router = useRouter();
  const [portfolioName, setPortfolioName] = useState(`Cloned from ${memberName}`);
  const [investmentAmount, setInvestmentAmount] = useState('10000');
  const [prices, setPrices] = useState<Record<string, number>>({});
  const [loadingPrices, setLoadingPrices] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [strategy, setStrategy] = useState<AllocationStrategy>(
    holdings.some((h) => (h.estimatedValue ?? 0) > 0) ? 'mirror' : 'equal',
  );
  const [skipUnaffordable, setSkipUnaffordable] = useState(true);

  // Fetch current prices on mount
  useEffect(() => {
    const tickers = holdings.map((h) => h.ticker);
    if (tickers.length === 0) { setLoadingPrices(false); return; }
    getStocksBatch(tickers)
      .then((results) => {
        const map: Record<string, number> = {};
        for (const r of results) {
          if (r.data) map[r.ticker.toUpperCase()] = r.data.price;
        }
        setPrices(map);
      })
      .catch(() => {})
      .finally(() => setLoadingPrices(false));
  }, [holdings]);

  const amount = parseFloat(investmentAmount) || 0;

  const allocations = useMemo(() => {
    return computeAllocations({ holdings, prices, amount, strategy, skipUnaffordable });
  }, [holdings, prices, amount, strategy, skipUnaffordable]);

  const unaffordable = allocations.filter((a) => a.price > 0 && !a.canAfford);
  const hasUnaffordable = unaffordable.length > 0;

  const spent = allocations.reduce((s, a) => s + a.spent, 0);
  const leftover = Math.max(0, amount - spent);
  const includedCount = allocations.filter((a) => a.shares > 0).length;

  const handleCreate = async () => {
    if (!isPro) { router.push('/pro'); return; }
    setCreating(true);
    setError('');
    try {
      const portfolio = await cloneCongressionalPortfolio({
        memberId,
        name: portfolioName,
        tickers: holdings.map((h) => h.ticker),
        allocations: allocations.filter((a) => a.shares > 0).map((a) => ({
          ticker: a.ticker,
          shares: a.shares,
          avgCost: a.price > 0 ? a.price : (a.spent || 0),
        })),
      });
      onCreated(portfolio.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create portfolio');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4" onClick={onClose}>
      <div
        className="bg-card-bg border border-default rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-default">
          <h3 className="text-base font-semibold text-white">Clone to Portfolio</h3>
          <button onClick={onClose} className="text-fg-subtle hover:text-fg transition-colors" aria-label="Close"><X size={16} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Portfolio name */}
          <div>
            <label className="text-xs text-fg-muted uppercase tracking-wide font-medium mb-1.5 block">Portfolio Name</label>
            <input
              value={portfolioName}
              onChange={(e) => setPortfolioName(e.target.value)}
              className="w-full bg-surface border border-default rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-sky-500/50"
            />
          </div>

          {/* Investment amount */}
          <div>
            <label className="text-xs text-fg-muted uppercase tracking-wide font-medium mb-1.5 block">Investment Amount</label>
            <div className="relative">
              <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-subtle" />
              <input
                type="number" value={investmentAmount}
                onChange={(e) => setInvestmentAmount(e.target.value)}
                min="0" step="1000" placeholder="10000"
                className="w-full bg-surface border border-default rounded-lg pl-8 pr-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-sky-500/50"
              />
            </div>
          </div>

          {/* Strategy picker */}
          <div>
            <label className="text-xs text-fg-muted uppercase tracking-wide font-medium mb-1.5 block">Allocation Strategy</label>
            <div className="flex rounded-lg overflow-hidden border border-default">
              <button
                onClick={() => setStrategy('equal')}
                className={cn('flex-1 px-3 py-2 text-xs font-medium transition-colors',
                  strategy === 'equal' ? 'bg-sky-500/20 text-sky-300' : 'bg-surface text-fg-muted hover:text-slate-200')}
              >
                Equal weight
              </button>
              <button
                onClick={() => setStrategy('mirror')}
                disabled={!holdings.some((h) => (h.estimatedValue ?? 0) > 0)}
                className={cn('flex-1 px-3 py-2 text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed',
                  strategy === 'mirror' ? 'bg-sky-500/20 text-sky-300' : 'bg-surface text-fg-muted hover:text-slate-200')}
                title={holdings.some((h) => (h.estimatedValue ?? 0) > 0) ? 'Weight by the member\'s disclosed position size' : 'No estimated values available — equal weight only'}
              >
                Mirror conviction
              </button>
            </div>
            <p className="text-[10px] text-fg-subtle mt-1.5">
              {strategy === 'equal'
                ? 'Split the budget evenly across every ticker; leftover dollars buy more of the cheapest stocks.'
                : 'Weight each stock by the member\'s disclosed position size. Bigger conviction = bigger slice.'}
            </p>
          </div>

          {/* Skip-unaffordable toggle */}
          <label className="flex items-center gap-2 text-xs text-fg-muted cursor-pointer select-none">
            <input
              type="checkbox"
              checked={skipUnaffordable}
              onChange={(e) => setSkipUnaffordable(e.target.checked)}
              className="accent-sky-500"
            />
            Skip stocks the budget can&apos;t afford and redistribute their share of the budget.
          </label>

          {/* Allocations table */}
          <div>
            <label className="text-xs text-fg-muted uppercase tracking-wide font-medium mb-1.5 block">
              Allocations ({includedCount} of {holdings.length} holdings)
            </label>
            {loadingPrices ? (
              <div className="flex items-center justify-center py-8 text-fg-subtle">
                <Loader2 size={16} className="animate-spin mr-2" /> Loading prices...
              </div>
            ) : (
              <div className="bg-surface border border-default rounded-lg overflow-hidden">
                <div className="grid grid-cols-[1fr_60px_80px_70px_70px] gap-2 px-3 py-2 text-[10px] text-fg-subtle uppercase tracking-wide font-medium border-b border-default">
                  <span>Ticker</span>
                  <span className="text-right">Weight</span>
                  <span className="text-right">Price</span>
                  <span className="text-right">Amount</span>
                  <span className="text-right">Shares</span>
                </div>
                <div className="max-h-52 overflow-y-auto divide-y divide-border-default/50">
                  {allocations.map((a) => (
                    <div key={a.ticker} className={cn('grid grid-cols-[1fr_60px_80px_70px_70px] gap-2 px-3 py-2 text-xs', a.price === 0 && 'bg-amber-500/5')}>
                      <span className="font-mono font-semibold text-sky-400">
                        {a.ticker}
                        {a.price === 0 && <span className="text-amber-400 ml-1" title="Price unavailable">*</span>}
                      </span>
                      <span className="text-right text-fg-muted">{(a.weight * 100).toFixed(1)}%</span>
                      <span className="text-right text-slate-300 font-mono">
                        {a.price > 0 ? `$${a.price.toFixed(2)}` : <span className="text-amber-400/70">N/A</span>}
                      </span>
                      <span className="text-right text-slate-300 font-mono">
                        {amount > 0 ? `$${a.spent.toFixed(0)}` : '\u2014'}
                      </span>
                      <span className="text-right text-white font-mono font-medium">
                        {!a.canAfford ? <span className="text-red-400 text-[10px]">—</span> : a.shares > 0 ? a.shares : '\u2014'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Summary */}
          {!loadingPrices && amount > 0 && (
            <div className="bg-surface border border-default rounded-lg px-3 py-2 text-xs text-fg-muted flex items-center justify-between">
              <span>Spent: <span className="font-mono text-white">${spent.toFixed(0)}</span></span>
              <span>Leftover cash: <span className="font-mono text-white">${leftover.toFixed(0)}</span></span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-default">
          {hasUnaffordable && !skipUnaffordable && (
            <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 mb-2">
              <span className="text-amber-400 text-xs">⚠</span>
              <p className="text-[11px] text-amber-300">
                {unaffordable.length} ticker{unaffordable.length === 1 ? '' : 's'} priced above their budget slice. Enable &ldquo;Skip stocks the budget can&apos;t afford&rdquo; to omit them, or increase the investment amount.
              </p>
            </div>
          )}
          {error && <p className="text-xs text-red-400 mb-2">{error}</p>}
          {isPro ? (
            <button
              onClick={handleCreate}
              disabled={creating || !portfolioName.trim() || amount <= 0 || loadingPrices || includedCount === 0}
              className="w-full bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white text-sm font-medium py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {creating && <Loader2 size={14} className="animate-spin" />}
              {creating ? 'Creating...' : 'Create Portfolio'}
            </button>
          ) : (
            <button
              onClick={() => router.push('/pro')}
              className="w-full bg-card-hover-bg border border-default text-fg-muted text-sm font-medium py-2.5 rounded-lg flex items-center justify-center gap-2"
            >
              <Lock size={14} /> Upgrade to Pro
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Allocation math ─────────────────────────────────────────────────────────

interface AllocationRow {
  ticker: string;
  weight: number;
  price: number;
  shares: number;
  spent: number;
  canAfford: boolean;
}

/**
 * Decide how many shares of each holding to buy for the given total budget.
 *
 * Strategies:
 *  - `equal`: split the budget evenly across every ticker
 *  - `mirror`: weight each ticker by the member's estimatedValue
 *
 * In both cases we compute a per-stock dollar slice, buy whole shares up to
 * that slice, pool the leftover cents, and spend the pool on the cheapest
 * affordable stocks. When `skipUnaffordable` is true, any stock whose price
 * exceeds its initial slice is dropped and its slice redistributed across
 * the remaining stocks — this is what keeps a 100-ticker / $10K clone from
 * producing a basket of 80 empty rows.
 */
function computeAllocations({
  holdings, prices, amount, strategy, skipUnaffordable,
}: {
  holdings: CloneHolding[];
  prices: Record<string, number>;
  amount: number;
  strategy: AllocationStrategy;
  skipUnaffordable: boolean;
}): AllocationRow[] {
  if (holdings.length === 0 || amount <= 0) {
    return holdings.map((h) => ({
      ticker: h.ticker, weight: 0,
      price: prices[h.ticker.toUpperCase()] ?? 0,
      shares: 0, spent: 0, canAfford: true,
    }));
  }

  // 1) Starting weights
  const withPrice = holdings.map((h) => ({
    ticker: h.ticker,
    price: prices[h.ticker.toUpperCase()] ?? 0,
    weight: strategyWeight(h, holdings, strategy),
  }));

  // 2) Optional redistribution of slices from stocks priced above their slice
  let weighted = withPrice;
  if (skipUnaffordable) {
    const affordable: typeof weighted = [];
    const droppedWeight = weighted.reduce((dropped, row) => {
      const slice = row.weight * amount;
      if (row.price > 0 && row.price > slice) return dropped + row.weight;
      affordable.push(row);
      return dropped;
    }, 0);
    if (affordable.length > 0 && droppedWeight > 0) {
      const totalAff = 1 - droppedWeight;
      weighted = weighted.map((row) => {
        const slice = row.weight * amount;
        const dropped = row.price > 0 && row.price > slice;
        if (dropped) return { ...row, weight: 0 };
        // Scale up proportionally so weights re-sum to 1 across survivors.
        return { ...row, weight: totalAff > 0 ? row.weight / totalAff : row.weight };
      });
    }
  }

  // 3) First pass: whole-share buy up to slice
  const items = weighted.map((row) => {
    const slice = row.weight * amount;
    const shares = row.price > 0 ? Math.floor(slice / row.price) : 0;
    const spent = shares * row.price;
    const canAfford = row.weight === 0 ? false : (row.price === 0 || row.price <= slice);
    return { ...row, shares, spent, canAfford };
  });

  // 4) Pool unspent cents + distribute on the cheapest affordable stocks
  let remainder = amount - items.reduce((s, i) => s + i.spent, 0);
  const cheapestFirst = [...items]
    .filter((i) => i.canAfford && i.price > 0)
    .sort((a, b) => a.price - b.price);
  for (const row of cheapestFirst) {
    while (remainder >= row.price) {
      row.shares += 1;
      row.spent += row.price;
      remainder -= row.price;
    }
  }

  return items.map((row) => ({
    ticker: row.ticker,
    weight: row.weight,
    price: row.price,
    shares: row.shares,
    spent: row.spent,
    canAfford: row.canAfford,
  }));
}

function strategyWeight(h: CloneHolding, all: CloneHolding[], strategy: AllocationStrategy): number {
  if (strategy === 'mirror') {
    const totalEst = all.reduce((s, x) => s + (x.estimatedValue ?? 0), 0);
    if (totalEst > 0) return (h.estimatedValue ?? 0) / totalEst;
  }
  return all.length > 0 ? 1 / all.length : 0;
}
